import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bookmark, BookmarkX, Loader2, Pencil, Tag as TagIcon } from "lucide-react";
import EditBrueckeModal from "@/components/EditBrueckeModal";
import { savedSectionTitle } from "@/constants/copy";

// Sprint 5a AC #3 — Saved Library: gespeicherte Flexpfade im Dashboard anzeigen.
// Sprint 8.A.1 — Erweitert um Tags + Notizen + Tag-Filter + Edit-Modal.

const FT_LABELS = { eule: "Eule", biene: "Biene", katze: "Katze", hund: "Hund" };

export default function SavedLibrary({ user }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const tier = user?.license_tier;
  const status = user?.license_status;
  const isPro =
    (status !== "expired" && status !== "cancelled") &&
    (tier === "pro_solo_monthly" ||
      tier === "pro_solo_annual" ||
      tier === "power_paket_onetime");
  const sectionTitle = savedSectionTitle({ isPro });
  // Sprint 8.E — Read-Only-Modus: cancelled User darf nicht editieren/unsaven
  const isReadOnly = status === "cancelled" || status === "expired";

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/anliegen/saved?limit=50");
      setItems(res.data?.items || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Konnte gespeicherte Flexpfade nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const allTags = useMemo(() => {
    const counts = {};
    for (const it of items) {
      for (const t of it.tags || []) {
        counts[t] = (counts[t] || 0) + 1;
      }
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [items]);

  const filtered = useMemo(() => {
    if (!activeTag) return items;
    return items.filter((it) => (it.tags || []).includes(activeTag));
  }, [items, activeTag]);

  const handleUnsave = async (e, id) => {
    e.stopPropagation();
    setRemovingId(id);
    const snapshot = items;
    setItems((prev) => prev.filter((it) => it.id !== id));
    try {
      const res = await api.post(`/anliegen/${id}/save`);
      if (res.data?.saved === true) {
        setItems(snapshot);
      }
    } catch (_err) {
      setItems(snapshot);
    } finally {
      setRemovingId(null);
    }
  };

  const handleEdit = (e, item) => {
    e.stopPropagation();
    setEditItem(item);
  };

  const handleSaved = (updated) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === updated.id
          ? { ...it, tags: updated.tags ?? it.tags, notes: updated.notes ?? it.notes }
          : it,
      ),
    );
    // If active tag no longer exists on items, clear filter
    if (activeTag && !(updated.tags || []).includes(activeTag)) {
      const stillUsed = items.some(
        (it) => it.id !== updated.id && (it.tags || []).includes(activeTag),
      );
      if (!stillUsed) setActiveTag(null);
    }
  };

  // Empty state
  if (!loading && items.length === 0 && !error) {
    return (
      <section
        id="saved-library"
        data-testid="saved-library"
        className="rounded-2xl border border-dashed border-slate-200 bg-white/40 p-5 sm:p-6"
      >
        <h2 className="flex items-center gap-2 text-base sm:text-lg font-extrabold tracking-tight text-[var(--fts-text-primary)]">
          <Bookmark className="w-4 h-4 text-slate-400" />
          {sectionTitle}
        </h2>
        <p className="mt-2 text-sm text-slate-500" data-testid="saved-library-empty">
          Noch keinen Flexpfad gespeichert. In jedem Flexpfad findest du den Button
          „In Bibliothek speichern" — damit landet der Flexpfad hier.
        </p>
      </section>
    );
  }

  return (
    <section
      id="saved-library"
      data-testid="saved-library"
      className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="flex items-center gap-2 text-base sm:text-lg font-extrabold tracking-tight text-[var(--fts-text-primary)]">
          <Bookmark className="w-4 h-4 text-[var(--fts-primary-deep)]" />
          {sectionTitle}
        </h2>
        {!loading && items.length > 0 && (
          <span data-testid="saved-library-counter" className="text-xs text-slate-500">
            {filtered.length}
            {activeTag ? ` / ${items.length}` : ""} Eintrag{filtered.length === 1 ? "" : "e"}
          </span>
        )}
      </div>

      {/* Tag-Filter-Chips */}
      {!loading && allTags.length > 0 && (
        <div
          data-testid="saved-library-tag-filter"
          className="mb-4 flex flex-wrap gap-1.5 items-center"
        >
          <TagIcon className="w-3.5 h-3.5 text-slate-400" />
          {allTags.map(([t, count]) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTag(activeTag === t ? null : t)}
              data-testid={`saved-library-tag-chip-${t}`}
              className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${
                activeTag === t
                  ? "bg-[var(--fts-primary-deep)] text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {t} <span className="opacity-60">·{count}</span>
            </button>
          ))}
          {activeTag && (
            <button
              type="button"
              onClick={() => setActiveTag(null)}
              data-testid="saved-library-tag-clear"
              className="text-xs text-slate-500 hover:text-slate-900 underline ml-1"
            >
              Filter entfernen
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="saved-library-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          Lade…
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600" data-testid="saved-library-error">
          {error}
        </p>
      )}

      {!loading && !error && (
        <ul className="space-y-2.5" data-testid="saved-library-list">
          {filtered.map((item) => (
            <li
              key={item.id}
              data-testid={`saved-library-item-${item.id}`}
              className="group flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition-colors cursor-pointer"
              onClick={() => navigate("/dashboard/anliegen")}
              title="Neuen Flexpfad anlegen"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <span>{FT_LABELS[item.verdacht_fokustyp] || "—"}</span>
                  <span aria-hidden="true">·</span>
                  <span>{item.modus === "praezise" ? "Präzise" : "Schnell"}</span>
                </div>
                <p className="mt-1 text-sm text-slate-900 line-clamp-2">
                  {item.bridge_text || item.situation || "—"}
                </p>
                {/* Tags */}
                {(item.tags || []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(item.tags || []).map((t) => (
                      <span
                        key={t}
                        data-testid={`saved-library-item-tag-${item.id}-${t}`}
                        className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                {/* Notes preview */}
                {item.notes && (
                  <p
                    data-testid={`saved-library-item-notes-${item.id}`}
                    className="mt-1.5 text-xs text-slate-500 italic line-clamp-1"
                  >
                    📝 {item.notes}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1 items-end">
                {!isReadOnly && (
                  <button
                    type="button"
                    aria-label="Tags + Notizen bearbeiten"
                    onClick={(e) => handleEdit(e, item)}
                    data-testid={`saved-library-edit-${item.id}`}
                    className="p-2 rounded-md text-slate-400 hover:text-[var(--fts-primary-deep)] hover:bg-slate-100 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                {!isReadOnly && (
                  <button
                    type="button"
                    aria-label="Aus Bibliothek entfernen"
                    onClick={(e) => handleUnsave(e, item.id)}
                    disabled={removingId === item.id}
                    data-testid={`saved-library-remove-${item.id}`}
                    className="p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-40"
                  >
                    <BookmarkX className="w-4 h-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <EditBrueckeModal
        open={!!editItem}
        item={editItem}
        isPro={isPro}
        onClose={() => setEditItem(null)}
        onSaved={handleSaved}
      />
    </section>
  );
}
